<template>
  <div
    class="index"
    :class="{ 'index--name-bucket': $route.name != 'bucket.tier3' }"
  >
    <div class="index__container">
      <tz-breadcrumbs :items="breadcrumbs" site-url="https://information.com" />
      <h1 class="index__title" v-text="pageTitle" />
      <ic-dynamic-freshness />
      <div class="index__list">
        <a
          v-for="(item, index) in list"
          :key="item.key"
          :href="item.url"
          class="index__link"
        >
          <span class="index__name" v-text="item.name" />
        </a>
      </div>
    </div>
  </div>
</template>

<script>
// Components
import TzBreadcrumbs from '@trazi/tz-breadcrumbs/src/tz-breadcrumbs.vue';
import IcDynamicFreshness from '@/components/ic-dynamic-freshness.vue';

// Helpers
import setTitleCase from '@/assets/js/shared/helpers/setTitleCase';
import seoSDK from '@/assets/js/shared/services/seo-sdk';
import getOptOutNames from '@/assets/js/shared/helpers/getOptOutNames';

const NBars = require('nbars/commonjs.js');

// CMS
const pageCms = require(`@/assets/cms/pages/name-bucket/letter.json`);

export default {
  name: 'index',
  components: {
    TzBreadcrumbs,
    IcDynamicFreshness
  },
  props: {
    cms: {
      type: Object,
      default: () => {
        return {
          page: pageCms,
          global: require(`@/assets/cms/global.json`)
        };
      }
    }
  },
  data: () => ({
    list: [],
    pageTitle: null,
    breadcrumbs: [],
    pageMeta: {}
  }),
  head() {
    return this.pageMeta;
  },
  async fetch() {
    const route = this.$route;
    const params = route.params;
    try {
      const searchParams = Object.values(params);
      const { NameRanges } = seoSDK;
      const [optouts, pageRes] = await Promise.all([
        getOptOutNames('|', this.$bugsnag),
        NameRanges.byLetter(...searchParams)
      ]);
      if (pageRes && optouts) {
        const path = pageRes.path;
        const nameRange = path?.length
          ? setTitleCase({ text: path[path.length - 1].replace(/\|/g, ' ') })
          : '';
        // set payload data
        this.list = this.formatNamesList(pageRes, optouts);
        this.breadcrumbs = this.formatBreadCrumbs(path);
        this.pageMeta = this.formatPageMeta(nameRange);
        this.pageTitle =
          route.name === 'bucket.letter'
            ? this.cms.page.title_letter + ' ' + params.letter.toUpperCase()
            : this.cms.page.title_letter_range + ' ' + nameRange;
      }
    } catch (err) {
      this.$bugsnag?.notify(err, event => {
        event.addMetadata('Extra Info', {
          page: this.$route.path,
          component: 'BucketIndex',
          method: 'fetch'
        });
      });
    }
  },
  methods: {
    /**
     * Formats list of names based on type of bucket page
     *
     * @param {object} pageRes - results of NameRanges.byLetter
     *  @param {array} optouts - results of getOptOutNames
     * @returns {array}
     */
    formatNamesList(pageRes, optouts) {
      if (!pageRes?.data) {
        return [];
      }
      // filter names list for any opted-out names
      const filtered = pageRes.data.filter(
        name => !optouts.includes(name.toLowerCase())
      );

      //format names list item
      const params = this.$route.params;
      const keyPrefix = Object.values(params).join('-');
      return filtered.map((item, index) => {
        const formatted = { key: `${keyPrefix}-${index}` };

        // Index starts at 0, 0 would be current page
        let urlIndex = index + 1;
        // Ensure padding exists on index
        if (urlIndex < 10) {
          urlIndex = '00' + urlIndex;
        } else if (urlIndex < 100) {
          urlIndex = '0' + urlIndex;
        }
        switch (this.$route.name) {
          case 'bucket.letter':
            formatted.name = item.replace(/\|/g, ' ');
            formatted.url = `/names/${params.letter}/${urlIndex}/`;
            break;
          case 'bucket.tier2':
            formatted.name = item.replace(/\|/g, ' ');
            formatted.url = `/names/${params.letter}/${params.tier2}/${urlIndex}/`;
            break;
          case 'bucket.tier3':
            const nameArray = item.split('|');
            const firstName = setTitleCase({ text: nameArray[0] });
            const lastName = setTitleCase({ text: nameArray[1] });
            formatted.name = `${firstName} ${lastName}`;
            const slugFirst = firstName.replace('-', '').toLowerCase();
            const slugLast = lastName.replace('-', '_').toLowerCase();
            formatted.url = `/people/${slugFirst}-${slugLast}/`;
        }
        // format url in snake case
        formatted.url = formatted.url.replace(/ /g, '_').toLowerCase();
        formatted.name = setTitleCase({ text: formatted.name });
        return formatted;
      });
    },
    /**
     * Formats text and link for each breadcrumb segment based on type of bucket page.
     * Last segment of breadcrumbs should be disabled
     *
     * @param {string} path - path passed from fetch res
     * @returns {array}
     */
    formatBreadCrumbs(path) {
      if (!path) {
        return [];
      }
      const routeName = this.$route.name;
      const params = this.$route.params;
      const paramsCount = Object.entries(params).length;
      const list = [{ href: '/', text: 'People Directory' }];

      if (paramsCount > 0) {
        list.push({
          href: `/names-by-letter/${params.letter}/`,
          text: `Last Name (${params.letter.toUpperCase()})`,
          disabled: routeName === 'bucket.letter'
        });
      }
      if (paramsCount > 1) {
        list.push({
          href: `/names/${params.letter}/${params.tier2}/`,
          text: setTitleCase({
            text: path[1].replace(/\|/g, ' ')
          }),
          disabled: routeName === 'bucket.tier2'
        });
      }
      if (paramsCount > 2) {
        list.push({
          href: `/names/${params.letter}/${params.tier2}/${params.tier3}/`,
          text: setTitleCase({ text: path[2] }),
          disabled: routeName === 'bucket.tier3'
        });
      }

      return list;
    },
    /**
     * Formats page metadata tags based on type of bucket page
     *
     * @param {string} nameRange
     * @returns {object {
     *            title: string,
     *            meta: object
     *          }}
     */
    formatPageMeta(nameRange) {
      const letter = this.$route.params.letter;
      const baseMeta =
        this.$route.name === 'bucket.letter'
          ? this.cms.page.meta_letter
          : this.cms.page.meta_letter_range;
      const transformedMeta = [];
      let title = '';
      switch (this.$route.name) {
        case 'bucket.letter':
          title = NBars.transform(this.cms.page.page_title_letter, {
            letter: letter.toUpperCase()
          });

          baseMeta.forEach(metaTag => {
            let transformConfig = {};

            switch (metaTag.hid) {
              case 'og:description':
              case 'description':
              case 'og:url':
              case 'url':
                transformConfig = { letter };
                break;
              case 'og:title':
              case 'apple-mobile-web-app-title':
                transformConfig = { letter: letter.toUpperCase() };
            }
            transformedMeta.push({
              ...metaTag,
              hid: metaTag.hid,
              content: NBars.transform(metaTag.content, transformConfig)
            });
          });
          break;
        case 'bucket.tier2':
        case 'bucket.tier3':
          title = NBars.transform(this.cms.page.page_title_letter_range, {
            nameRange
          });
          baseMeta.forEach(metaTag => {
            let transformConfig = {};
            switch (metaTag.hid) {
              case 'og:description':
              case 'description':
              case 'og:title':
              case 'apple-mobile-web-app-title':
                transformConfig = { nameRange };
                break;
              case 'og:url':
              case 'url':
                const { params } = this.$route;
                const tierCode =
                  this.$route.name === 'bucket.tier2'
                    ? params.tier2
                    : `${params.tier2}/${params.tier3}`;
                transformConfig = {
                  letter,
                  tierCode
                };
            }
            transformedMeta.push({
              ...metaTag,
              hid: metaTag.hid,
              content: NBars.transform(metaTag.content, transformConfig)
            });
          });
      }

      return {
        title,
        meta: transformedMeta
      };
    }
  }
};
</script>
